import { nanoid } from '@reduxjs/toolkit'
import { useToast } from '@chakra-ui/react'
import { RefObject, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import type { AppThunk, RootState } from '../../app/store'
import { RecentMedicationRequestsModal } from './RecentMedicationRequestsModal'
import { ehrLaunchPatientApi } from './ehrLaunchPatientApi'
import { expireEhrSession } from './ehrPatientAcquisitionSlice'
import {
  ProductResult,
  ProductSearchClient,
  clearProductSearch,
  initializeProductSearch,
} from '../../pages/NewScriptPage/ScriptForm/ProductSearchContextSlice'
import {
  addPrescribedItem,
  commitEhrPrescribedItem,
} from '../../pages/NewScriptPage/ScriptForm/PrescribedItem/PrescribedItemSlice'
import { prepareMimsPrescribedItem } from '../../pages/NewScriptPage/ScriptForm/PrescribedItem/utils/prepareMimsPrescribedItem'
import { FeatureKey } from '../../types/organization-entitlements'
import { FundingSource } from '../../types/organization'
import {
  dismissEhrRecentMedicationRequests,
  ehrRecentMedicationRequestsReady,
  ehrRecentMedicationRequestsStarted,
  ehrReprescribingCommitted,
  ehrReprescribingFailed,
  ehrReprescribingSearchOpened,
  ehrReprescribingStarted,
} from './ehrRecentMedicationRequestsSlice'

interface EhrRecentMedicationRequestsControllerProps {
  finalFocusRef: RefObject<HTMLElement | null>
  workspaceFocusRef?: RefObject<HTMLElement | null>
  onOpenProductDetails?: () => void
}

type ReprescribingOutcome =
  | { destination: 'review'; warning?: string }
  | { destination: 'search' }
  | undefined

const currentAttempt = (state: RootState, launchInstanceId: string, attemptId: string) =>
  state.authenticatedProvider === 'ehr' &&
  state.ehrPatientAcquisition.status === 'resolved' &&
  !state.ehrPatientAcquisition.focusVerificationRequired &&
  state.ehrPatientAcquisition.launchInstanceId === launchInstanceId &&
  state.ehrPatientAcquisition.binding?.launchInstanceId === launchInstanceId &&
  state.ehrRecentMedicationRequests.launchInstanceId === launchInstanceId &&
  state.ehrRecentMedicationRequests.represcribing?.phase === 'matching' &&
  state.ehrRecentMedicationRequests.represcribing.attemptId === attemptId

const destinationFor = (
  state: RootState
): { index: number; append: boolean } | { message: string } => {
  const script = state.newScript
  if (script.prescribedItemList[script.selectedPrescribedItemIndex] === undefined) {
    return { index: script.selectedPrescribedItemIndex, append: false }
  }
  if (state.billing.subscription.error) {
    return { message: 'Your subscription must be active before adding another medication.' }
  }
  if (!state.organization.entitlements?.entitlements?.[FeatureKey.MULTIPLE_SCRIPTS]) {
    return { message: 'Your plan allows one medication per prescription.' }
  }
  if (script.prescribedItemList.length >= 15) {
    return { message: 'A prescription can contain at most 15 medications.' }
  }
  return { index: script.prescribedItemList.length, append: true }
}

export const startEhrReprescribing =
  (
    launchInstanceId: string,
    rowIndex: number,
    forceTextSearch = false
  ): AppThunk<Promise<ReprescribingOutcome>> =>
  async (dispatch, getState) => {
    const result = getState().ehrRecentMedicationRequests.result
    const medication = result?.status === 'available' ? result.medications[rowIndex] : undefined
    if (!medication || medication.represcribing.eligibility !== 'eligible') return

    const attemptId = nanoid()
    dispatch(ehrReprescribingStarted({ launchInstanceId, rowIndex, attemptId }))
    if (!currentAttempt(getState(), launchInstanceId, attemptId)) return

    const fail = (message: string) => {
      dispatch(ehrReprescribingFailed({ launchInstanceId, attemptId, message }))
      return undefined
    }
    const initialDestination = destinationFor(getState())
    if ('message' in initialDestination) return fail(initialDestination.message)
    const openSearch = (products: ProductResult[]): ReprescribingOutcome => {
      if (!currentAttempt(getState(), launchInstanceId, attemptId)) return undefined
      const destination = destinationFor(getState())
      if ('message' in destination) return fail(destination.message)
      if (destination.append) dispatch(addPrescribedItem(undefined))
      dispatch(
        initializeProductSearch({
          searchTerm: medication.represcribing.searchText ?? medication.name,
          productResults: products,
        })
      )
      dispatch(
        ehrReprescribingSearchOpened({
          launchInstanceId,
          attemptId,
          slotIndex: destination.index,
          prefill: medication.represcribing.prefill ?? {},
        })
      )
      return { destination: 'search' }
    }

    try {
      let products: ProductResult[]
      const coding = medication.represcribing.medicineCoding
      if (!forceTextSearch && coding) {
        products = (
          await ProductSearchClient.searchProduct({
            queryTerm: coding.code,
            pageNumber: 1,
            pageOffset: 30,
          })
        ).products
        if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
        if (products.length === 1) {
          const details = await ProductSearchClient.getProductDetails({
            id: products[0].productId,
            productType: products[0].productType,
          })
          if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
          const prepared = prepareMimsPrescribedItem(details.product, {
            ctgFlag: getState().patientDetail.selectedPatient?.ctg_flag,
            isAbleToSelectS8Medicine: !!getState().organization.data?.settings?.enableS8Medicine,
            ehrPrefill: medication.represcribing.prefill,
          })
          if (!prepared.passed) {
            return fail(
              prepared.message ??
                'This medicine cannot be selected. Search by the EHR name to choose another product.'
            )
          }
          const destination = destinationFor(getState())
          if ('message' in destination) return fail(destination.message)
          if (destination.append) dispatch(addPrescribedItem(undefined))
          dispatch(clearProductSearch())
          dispatch(commitEhrPrescribedItem({ index: destination.index, item: prepared.item }))
          dispatch(ehrReprescribingCommitted({ launchInstanceId, attemptId }))
          return { destination: 'review', warning: prepared.message }
        }
        if (products.length > 1) return openSearch(products)
      }

      const searchText = medication.represcribing.searchText
      if (!searchText) return fail('The EHR medicine name is unavailable for product search.')
      products = (
        await ProductSearchClient.searchProduct({
          queryTerm: searchText,
          pageNumber: 1,
          pageOffset: 30,
        })
      ).products
      return openSearch(products)
    } catch {
      return fail('Medicine lookup failed. You can search by the EHR name.')
    }
  }

const isCurrentWaitingLaunch = (state: RootState, launchInstanceId: string) => {
  const acquisition = state.ehrPatientAcquisition
  const recent = state.ehrRecentMedicationRequests
  return (
    state.authenticatedProvider === 'ehr' &&
    acquisition.status === 'resolved' &&
    acquisition.launchInstanceId === launchInstanceId &&
    acquisition.binding?.launchInstanceId === launchInstanceId &&
    recent.launchInstanceId === launchInstanceId &&
    recent.phase === 'loading'
  )
}

const httpStatus = (error: unknown) =>
  (error as { status?: number; response?: { status?: number } })?.response?.status ??
  (error as { status?: number })?.status

export const retrieveRecentMedicationRequestsForLaunch =
  (launchInstanceId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const acquisition = getState().ehrPatientAcquisition
    const recent = getState().ehrRecentMedicationRequests
    if (
      getState().authenticatedProvider !== 'ehr' ||
      acquisition.status !== 'resolved' ||
      acquisition.focusVerificationRequired ||
      acquisition.launchInstanceId !== launchInstanceId ||
      acquisition.binding?.launchInstanceId !== launchInstanceId ||
      (recent.launchInstanceId === launchInstanceId && recent.phase !== 'idle')
    )
      return

    dispatch(ehrRecentMedicationRequestsStarted({ launchInstanceId }))
    const request = dispatch(
      ehrLaunchPatientApi.endpoints.retrieveRecentMedicationRequests.initiate()
    )
    try {
      const result = await request.unwrap()
      if (!isCurrentWaitingLaunch(getState(), launchInstanceId)) return
      dispatch(ehrRecentMedicationRequestsReady({ launchInstanceId, result }))
    } catch (error) {
      if (!isCurrentWaitingLaunch(getState(), launchInstanceId)) return
      if (httpStatus(error) === 401 || httpStatus(error) === 403) {
        dispatch(expireEhrSession())
        return
      }
      dispatch(
        ehrRecentMedicationRequestsReady({
          launchInstanceId,
          result: { status: 'unavailable' },
        })
      )
    } finally {
      request.reset()
    }
  }

export const EhrRecentMedicationRequestsController = ({
  finalFocusRef,
  workspaceFocusRef,
  onOpenProductDetails,
}: EhrRecentMedicationRequestsControllerProps) => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const provider = useAppSelector((state) => state.authenticatedProvider)
  const acquisition = useAppSelector((state) => state.ehrPatientAcquisition)
  const recent = useAppSelector((state) => state.ehrRecentMedicationRequests)
  const launchInstanceId = acquisition.launchInstanceId
  const defaultFundingSource = useAppSelector(
    (state) => state.organization.data?.settings?.defaultFundingSource
  )
  const eligible =
    provider === 'ehr' &&
    acquisition.status === 'resolved' &&
    !acquisition.focusVerificationRequired &&
    !!launchInstanceId &&
    acquisition.binding?.launchInstanceId === launchInstanceId

  useEffect(() => {
    if (!eligible || !launchInstanceId) return
    void dispatch(retrieveRecentMedicationRequestsForLaunch(launchInstanceId))
  }, [dispatch, eligible, launchInstanceId])

  if (
    !eligible ||
    !launchInstanceId ||
    recent.phase !== 'ready' ||
    recent.launchInstanceId !== launchInstanceId ||
    !recent.result
  )
    return null

  return (
    <RecentMedicationRequestsModal
      result={recent.result}
      finalFocusRef={finalFocusRef}
      represcribing={recent.represcribing}
      onReprescribe={async (rowIndex, forceTextSearch) => {
        const outcome = await dispatch(
          startEhrReprescribing(launchInstanceId, rowIndex, forceTextSearch)
        )
        if (outcome?.destination === 'review' && outcome.warning) {
          toast({
            title: 'Warning',
            description: outcome.warning,
            status: 'warning',
            duration: null,
            isClosable: true,
            position: 'top',
          })
        }
        if (
          outcome?.destination === 'review' &&
          defaultFundingSource !== FundingSource.PRIVATE &&
          onOpenProductDetails
        ) {
          onOpenProductDetails()
        } else if (outcome) {
          setTimeout(() => {
            workspaceFocusRef?.current?.scrollIntoView({ block: 'start' })
            workspaceFocusRef?.current?.focus()
          }, 0)
        }
      }}
      onDismiss={() => dispatch(dismissEhrRecentMedicationRequests(launchInstanceId))}
    />
  )
}
